import { Suspense, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { api } from "../../lib/api";
import { getAuthToken } from "../../lib/auth";
import { toast } from "../../lib/ui";
import { FriendsModal, InputModal, CreateSpaceModal, QuickChannelModal } from "../../components/modals";
import { GifPicker } from "../../components/media";
import AndroidShareModal from "../../components/chat/AndroidShareModal";
import MediaLightbox from "../../components/media/MediaLightbox";
import NotificationCenter from "../../components/notifications/NotificationCenter";
import UserQuickSettings from "../../components/settings/UserQuickSettings";
import { useMediaLightbox } from "../../hooks/useMediaLightbox";
import type { useChannelLocks } from "../../hooks/useChannelLocks";
import type { useDirectCall } from "../calls/useDirectCall";
import type { AuthUser } from "../../types/auth";
import type { Channel } from "./chatTypes";
import type { useAndroidShare } from "./hooks/useAndroidShare";
import type { useDms } from "./hooks/useDms";
import type { useFriends } from "./hooks/useFriends";
import type { useInputModal } from "./hooks/useInputModal";
import type { useMediaPickers } from "./composer/useMediaPickers";
import type { useNotifications } from "./hooks/useNotifications";
import type { useQuickChannel } from "./hooks/useQuickChannel";
import type { useSpaces } from "./hooks/useSpaces";
import type { useUserStatusMessage } from "./hooks/useUserStatusMessage";

type Pickable<T extends (...a: any[]) => any, K extends keyof ReturnType<T>> = Pick<ReturnType<T>, K>;

type Member = { id: string; name: string; status?: string; [key: string]: unknown };

/** Modals, popovers and floating buttons rendered next to the main chat column. Pure presentation: all state stays in ChatLayout. */
export type ChatOverlaysProps =
  Pickable<typeof useMediaLightbox, "imagePreview" | "setImagePreview" | "imagePreviewVisible" | "imagePreviewList" | "imagePreviewIndex" | "setImagePreviewIndex" | "closeImagePreview" | "videoPreview" | "videoPreviewVisible" | "closeVideoPreview"> &
  Pickable<typeof useFriends, "friendsOpen" | "setFriendsOpen"> &
  Pickable<typeof useQuickChannel, "quickChannelOpen" | "setQuickChannelOpen" | "quickChannelBusy" | "channelTypeOptions" | "handleQuickChannelCreate"> &
  Pickable<typeof useSpaces, "createSpaceOpen" | "setCreateSpaceOpen" | "handleCreateSpace" | "joinSpaceWithCode" | "searchSpaces" | "requestJoinSpace" | "isSpaceOwner" | "canAccessChannel" | "voids"> &
  Pickable<typeof useInputModal, "inputOpen" | "inputCfg" | "closeInput"> &
  Pickable<typeof useChannelLocks, "unlockModal" | "unlockingChannel" | "handleUnlockChannel" | "closeUnlockModal"> &
  Pickable<typeof useNotifications, "notificationsOpen" | "setNotificationsOpen" | "notificationItems" | "markAllRead" | "removeNotification" | "friendReqCount" | "unreadNotificationCount"> &
  Pickable<typeof useMediaPickers, "gifOpen" | "setGifOpen"> &
  Pickable<typeof useUserStatusMessage, "userStatusMessage" | "updateUserStatusMessage"> &
  Pick<ReturnType<typeof useDms>, "startDmWithUser" | "getDmDisplayName"> & {
    globalUserIds: Parameters<typeof useFriends>[0]["globalUserIds"];
    directCall: ReturnType<typeof useDirectCall>;
    androidShare: ReturnType<typeof useAndroidShare>;
    currentVoidId: string;
    currentChannelId: string;
    channels: Channel[];
    isMessageChannelType: boolean;
    voiceJoined: boolean;
    showJump: boolean;
    setShowJump: (v: boolean) => void;
    listRef: MutableRefObject<HTMLDivElement | null>;
    setUnreadMarkerByChan: Dispatch<SetStateAction<Record<string, any>>>;
    userQuickOpen: boolean;
    setUserQuickOpen: (v: boolean) => void;
    me: { userId?: string; name?: string; username?: string | null; avatarUrl?: string | null };
    user: AuthUser | null;
    members: Member[];
    setMembers: Dispatch<SetStateAction<any[]>>;
    onOpenSettings: (section?: string) => void;
    onOpenProfile: (targetId: string) => void;
    markAllChannelsRead: () => void;
    addRemoteAsUpload: (remoteUrl: string, fallbackName?: string) => Promise<unknown>;
    switchVoid: (id: string) => void;
    openChannelFromFqid: (fqid: string, fallbackVoidId?: string) => void;
    fq: (vId: string, cId: string) => string;
  };

export function ChatOverlays({
  imagePreview, setImagePreview, imagePreviewVisible, imagePreviewList, imagePreviewIndex, setImagePreviewIndex, closeImagePreview,
  videoPreview, videoPreviewVisible, closeVideoPreview, friendsOpen, setFriendsOpen, globalUserIds, startDmWithUser, directCall,
  quickChannelOpen, setQuickChannelOpen, quickChannelBusy, channelTypeOptions, handleQuickChannelCreate, isSpaceOwner,
  createSpaceOpen, setCreateSpaceOpen, handleCreateSpace, joinSpaceWithCode, searchSpaces, requestJoinSpace,
  inputOpen, inputCfg, closeInput, unlockModal, unlockingChannel, handleUnlockChannel, closeUnlockModal,
  showJump, setShowJump, currentVoidId, currentChannelId, isMessageChannelType, voiceJoined, listRef, setUnreadMarkerByChan, fq,
  notificationsOpen, setNotificationsOpen, notificationItems, markAllRead, removeNotification,
  userQuickOpen, setUserQuickOpen, me, user, members, setMembers, friendReqCount, unreadNotificationCount, userStatusMessage,
  updateUserStatusMessage, onOpenSettings, onOpenProfile, markAllChannelsRead,
  androidShare, voids, channels, canAccessChannel, getDmDisplayName, switchVoid, openChannelFromFqid,
  gifOpen, setGifOpen, addRemoteAsUpload,
}: ChatOverlaysProps) {
  return (
    <>
      <MediaLightbox
        imagePreview={imagePreview}
        imagePreviewList={imagePreviewList}
        imagePreviewIndex={imagePreviewIndex}
        imagePreviewVisible={imagePreviewVisible}
        onClose={closeImagePreview}
        onSelectIndex={setImagePreviewIndex}
        onSelectPreview={setImagePreview}
        videoPreview={videoPreview}
        videoPreviewVisible={videoPreviewVisible}
        onCloseVideo={closeVideoPreview}
      />
      <FriendsModal
        token={getAuthToken() || ''}
        open={friendsOpen}
        onlineIds={globalUserIds}
        onClose={()=>setFriendsOpen(false)}
        onStartDm={async (uid) => {
          await startDmWithUser(uid);
          setFriendsOpen(false);
        }}
        onStartCall={async (uid, displayName) => {
          const ok = await directCall.startCall(uid, displayName || 'Direct contact');
          if (ok) setFriendsOpen(false);
        }}
      />

      <QuickChannelModal
        open={quickChannelOpen && isSpaceOwner}
        busy={quickChannelBusy}
        onClose={() => { if (!quickChannelBusy) setQuickChannelOpen(false); }}
        types={channelTypeOptions}
        onSubmit={(name, type) => { if (!quickChannelBusy) handleQuickChannelCreate(name, type); }}
      />

      <CreateSpaceModal
        open={createSpaceOpen}
        onCancel={() => setCreateSpaceOpen(false)}
        onSubmit={handleCreateSpace}
        onJoin={joinSpaceWithCode}
        onSearchSpaces={searchSpaces}
        onRequestJoin={requestJoinSpace}
      />

  <InputModal
        open={inputOpen}
        title={inputCfg.title}
        label={inputCfg.label}
        placeholder={inputCfg.placeholder}
        initialValue={inputCfg.initialValue}
        textarea={inputCfg.textarea}
        okText={inputCfg.okText}
        onSubmit={(v)=> closeInput(v || '')}
        onCancel={()=> closeInput(null)}
  />
  <InputModal
        open={!!unlockModal}
        title={`Unlock ${unlockModal?.channelName || 'channel'}`}
        label="Channel passcode"
        placeholder="Enter passcode"
        okText={unlockingChannel ? 'Unlocking…' : 'Unlock'}
        onSubmit={(val) => { if (!unlockingChannel) handleUnlockChannel(val); }}
        onCancel={closeUnlockModal}
  />
	  {showJump && currentVoidId && isMessageChannelType && (
    <button
      aria-label="Jump to present"
      className="md:hidden fixed right-4 z-40 px-3 py-2 rounded-full bg-emerald-700 text-white shadow-lg border border-emerald-600"
      style={{ bottom: voiceJoined ? 'calc(5.5rem + 68px + env(safe-area-inset-bottom))' : 'calc(5.5rem + env(safe-area-inset-bottom))' }}
      onClick={() => {
        const el = listRef.current; if (!el) return; el.scrollTop = el.scrollHeight; setShowJump(false);
        const cid = fq(currentVoidId, currentChannelId); setUnreadMarkerByChan(prev => ({ ...prev, [cid]: null }));
      }}
    >
      Jump to present
    </button>
  )}

      {/* Notification center */}
      <NotificationCenter
        open={notificationsOpen}
        items={notificationItems}
        onClose={() => setNotificationsOpen(false)}
        onMarkAllRead={markAllRead}
        onDismiss={removeNotification}
      />

      {/* Quick settings popover */}
      <UserQuickSettings
        open={userQuickOpen}
        name={me?.name || 'Me'}
        username={(() => { try { return JSON.parse(localStorage.getItem('user')||'{}')?.username || ''; } catch { return ''; } })()}
        avatarUrl={me?.avatarUrl || null}
        presence={(() => { const meMem = members.find(m => m.id === me.userId); const p = String(meMem?.status || 'online'); return (p as any) })()}
        friendRequestCount={friendReqCount}
        notificationCount={unreadNotificationCount}
        statusText={userStatusMessage}
        onClose={() => { setUserQuickOpen(false); }}
        onOpenFriends={() => { setUserQuickOpen(false); setFriendsOpen(true); }}
        onOpenSettings={() => { onOpenSettings(); setUserQuickOpen(false); }}
        onEditProfile={() => { onOpenSettings('profile'); setUserQuickOpen(false); }}
        onOpenNotifications={() => { setUserQuickOpen(false); setNotificationsOpen(true); }}
        onMarkAllRead={() => { markAllChannelsRead(); setUserQuickOpen(false); }}
        onUpdateStatus={updateUserStatusMessage}
        onOpenProfile={() => {
          const userId = me?.userId || user?.userId;
          if (!userId) {
            toast('Your profile is still loading. Please try again.', 'info');
            return;
          }
          onOpenProfile(userId);
        }}
        onChangePresence={async (p) => {
          try {
            const tok = getAuthToken() || '';
            const u = await api.patchAuth('/users/me', { status: p }, tok);
            setMembers(prev => prev.map(m => m.id === me.userId ? { ...m, status: u?.status || p } : m));
            try {
              const raw = localStorage.getItem('user');
              const prev = raw ? JSON.parse(raw) : {};
              localStorage.setItem('user', JSON.stringify({ ...prev, status: u?.status || p }));
            } catch { /* noop */ }
            setUserQuickOpen(false);
          } catch {
            // no-op; leave open for another try
          }
        }}
      />
      {androidShare.open && androidShare.payload && (
        <AndroidShareModal
          payload={androidShare.payload}
          spaceId={androidShare.spaceId}
          channelId={androidShare.channelId}
          currentVoidId={currentVoidId}
          voids={voids}
          accessibleChannels={channels.filter((c) => canAccessChannel(c))}
          channelCount={channels.length}
          getDmDisplayName={getDmDisplayName}
          onSelectSpace={(next) => { androidShare.selectSpace(next); if (next && next !== currentVoidId) switchVoid(next); }}
          onSetChannelId={(id) => androidShare.setChannelId(id)}
          onCancel={androidShare.cancel}
          onShare={() => { const target = androidShare.confirm(); if (!target) return; const fqid = fq(target.spaceId, target.channelId); openChannelFromFqid(fqid, target.spaceId); }}
        />
      )}
      <Suspense>
        <GifPicker
          open={gifOpen}
          onClose={() => setGifOpen(false)}
          onPick={async (g) => {
            // Import GIF into our storage to avoid external CSP/CORS issues
            await addRemoteAsUpload(g.url, 'gif.gif');
          }}
        />
      </Suspense>
    </>
  );
}
